import type { SupabaseClient } from "@supabase/supabase-js";
import { signKeys } from "./files";

/**
 * Playing the thirty-second summary when a parent opens an activity.
 *
 * ── The autoplay problem, and why this is not just an <audio autoplay> ──────
 * Browsers refuse programmatic playback that is not traceable to a user
 * gesture, and on iOS the refusal is silent. Opening an activity IS a tap, but
 * by the time React has re-rendered the modal, an effect has run, a row has been
 * fetched and a URL has been signed, the gesture is long over and the play is
 * rejected. The feature would work on a developer's desktop and fail on the
 * phones every family actually uses.
 *
 * Two things fix it, and both are needed:
 *
 *   1. One long-lived <audio> element, unlocked once on the first tap anywhere
 *      in the app. After that, playback on it is permitted for the session.
 *   2. URLs signed in advance, in a batch, when the day's plan loads. At open
 *      time there is nothing left to await, so play() is immediate.
 *
 * ── Off unless asked for ────────────────────────────────────────────────────
 * Default off. A voice starting on its own is startling, and this app is often
 * open beside a sleeping child or in a room with other people. A parent who
 * wants it turns it on once, per device — which is the right scope, because
 * wanting narration on your own phone says nothing about the shared tablet.
 */

const STORAGE_KEY = "alnoor-homeschool-narration";

export function narrationEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    // Private browsing, or storage blocked entirely. Off is the safe answer.
    return false;
  }
}

export function setNarrationEnabled(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  } catch {
    // Nothing to do. The toggle simply will not persist across a reload.
  }
}

let element: HTMLAudioElement | null = null;
let unlocked = false;

function audio(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (!element) {
    element = new Audio();
    element.preload = "none";
  }
  return element;
}

/**
 * Spend the first tap on permission.
 *
 * Called from a real listener on the first interaction anywhere. Playing and
 * immediately pausing an empty element is the standard way to convert a
 * gesture into a lasting permission; it makes no sound.
 */
export function unlockNarration() {
  if (unlocked) return;
  const player = audio();
  if (!player) return;
  unlocked = true;
  player.muted = true;
  void player
    .play()
    .then(() => {
      player.pause();
      player.muted = false;
    })
    .catch(() => {
      // Refused. Playback will still work from inside a later gesture; it just
      // will not start on its own. Nothing to report to the parent.
      player.muted = false;
    });
}

/** Stop whatever is playing — on closing an activity, or opening another. */
export function stopNarration() {
  const player = element;
  if (!player) return;
  player.pause();
  player.currentTime = 0;
}

/**
 * Play a clip. Returns false when the browser refused, so a caller can offer a
 * button instead of pretending it worked.
 */
export async function playNarration(url: string): Promise<boolean> {
  const player = audio();
  if (!player) return false;
  try {
    if (player.src !== url) player.src = url;
    player.currentTime = 0;
    await player.play();
    return true;
  } catch {
    return false;
  }
}

export type NarrationClip = { activityId: string; url: string; script: string };

/**
 * Signed URLs for a set of activities, in one round trip.
 *
 * Fetched when the day loads rather than when an activity opens, so that the
 * open is instant and inside the tap. Failure is deliberately quiet: narration
 * is an extra, and a family whose clips have not been generated yet should see
 * the app behave exactly as it did before this existed.
 */
export async function loadNarration(
  supabase: SupabaseClient,
  activityIds: string[],
  locale: string,
): Promise<Map<string, NarrationClip>> {
  const clips = new Map<string, NarrationClip>();
  if (activityIds.length === 0) return clips;
  try {
    const { data, error } = await supabase
      .from("homeschool_activity_audio")
      .select("activity_id,storage_path,script")
      .in("activity_id", activityIds)
      .eq("locale", locale);
    if (error || !data || data.length === 0) return clips;

    const rows = data as { activity_id: string; storage_path: string; script: string }[];
    const signed = await signKeys(supabase, rows.map((row) => row.storage_path));
    for (const row of rows) {
      const url = signed[row.storage_path];
      if (url) clips.set(row.activity_id, { activityId: row.activity_id, url, script: row.script });
    }
  } catch {
    // The table may not exist yet on a deployment where the migration has not
    // been applied. That is not an error a parent should ever see.
  }
  return clips;
}
